import React, { useState, useEffect } from 'react';
import { generateService, TweetCandidate } from '../services/generateService';
import { storageService } from '../services/storageService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import { handlePostToX } from '../utils/handlePostToX';
import { useToast } from './Toast';

interface IdeaBlock {
  id: string;
  content: string;
}

const STORAGE_KEY_BLOCKS = 'compose_blocks';
const STORAGE_KEY_FINAL = 'compose_final_tweet';
const STORAGE_KEY_VARIATIONS = 'compose_variations';

export const ComposeIdeas: React.FC = () => {
  const { t } = useT();
  const { toast } = useToast();
  const [blocks, setBlocks] = useState<IdeaBlock[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_BLOCKS);
    return saved ? JSON.parse(saved) : [{ id: '1', content: '' }];
  });
  const [finalTweet, setFinalTweet] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_FINAL) || '';
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVariations, setGeneratedVariations] = useState<TweetCandidate[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_VARIATIONS);
    return saved ? JSON.parse(saved) : [];
  });
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY_BLOCKS, JSON.stringify(blocks)); }, [blocks]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_FINAL, finalTweet); }, [finalTweet]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_VARIATIONS, JSON.stringify(generatedVariations)); }, [generatedVariations]);

  const addBlock = () => setBlocks([...blocks, { id: Date.now().toString(), content: '' }]);
  const updateBlock = (id: string, content: string) => setBlocks(blocks.map(b => b.id === id ? { ...b, content } : b));
  const removeBlock = (id: string) => { if (blocks.length > 1) setBlocks(blocks.filter(b => b.id !== id)); };

  const combineBlocks = () => {
    const combined = blocks.filter(b => b.content.trim()).map(b => b.content.trim()).join('\n\n');
    setFinalTweet(combined);
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const generateVariations = async () => {
    if (!finalTweet.trim()) return;
    setIsGenerating(true);
    try {
      const styleModel = storageService.getStyleModel();
      const buzzModel = storageService.getBuzzModel();
      const result = await generateService.generateTweets(finalTweet, styleModel?.systemPrompt || '', buzzModel?.insights || []);
      setGeneratedVariations(result.candidates);
      if (result.candidates.length > 0) {
        storageService.addHistoryItem({ type: 'tweet', content: result.candidates[0].text, score: result.candidates[0].score, metadata: { blocksCount: blocks.length } });
      }
    } catch (error) {
      console.error('Generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="mb-6 lg:mb-[30px]">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1.5 tracking-tight text-text-primary">{t('composeIdeas.title')}</h1>
        <p className="text-text-muted text-sm sm:text-[15px] leading-relaxed">{t('composeIdeas.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left: Idea Blocks */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg lg:text-xl m-0 text-text-primary">{t('composeIdeas.ideaBlocks')}</h2>
            <button onClick={addBlock} className="px-4 py-2 bg-brand border-none rounded-xl text-white cursor-pointer text-sm font-bold hover:bg-brand-hover transition-colors">
              {t('composeIdeas.addBlock')}
            </button>
          </div>

          {blocks.map((block, index) => (
            <div key={block.id} className="mb-3 p-4 bg-surface-2 border border-border rounded-xl">
              <div className="flex justify-between mb-2">
                <span className="text-xs text-text-muted font-bold">{t('composeIdeas.blockLabel', { n: index + 1 })}</span>
                {blocks.length > 1 && (
                  <button onClick={() => removeBlock(block.id)} className="bg-transparent border-none text-danger cursor-pointer text-sm">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                )}
              </div>
              <textarea
                value={block.content}
                onChange={(e) => updateBlock(block.id, e.target.value)}
                placeholder={t('composeIdeas.blockPlaceholder', { n: index + 1 })}
                className="w-full min-h-[100px] p-3 bg-surface-1 border border-border-subtle rounded-lg text-text-primary text-sm resize-y font-[inherit] placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              />
            </div>
          ))}

          <button onClick={combineBlocks} className="w-full py-3.5 bg-accent border-none rounded-xl text-white cursor-pointer text-base font-bold mt-2 hover:bg-accent/90 transition-colors">
            {t('composeIdeas.combineBlocks')}
          </button>
        </div>

        {/* Right: Combined & Variations */}
        <div>
          <h2 className="text-lg lg:text-xl mb-4 text-text-primary">{t('composeIdeas.combinedTweet')}</h2>
          <textarea
            value={finalTweet}
            onChange={(e) => setFinalTweet(e.target.value)}
            placeholder={t('composeIdeas.combinedPlaceholder')}
            className="w-full min-h-[200px] p-4 lg:p-5 bg-surface-2 border-2 border-accent rounded-xl text-text-primary text-base resize-y font-[inherit] leading-[1.6] shadow-glow-accent placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />

          <button
            onClick={generateVariations}
            disabled={!finalTweet.trim() || isGenerating}
            className={cn(
              'w-full py-3.5 mt-4 border-none rounded-3xl text-base font-bold transition-all',
              !finalTweet.trim() || isGenerating
                ? 'bg-surface-3 text-text-faint cursor-not-allowed'
                : 'bg-gradient-to-r from-accent to-purple-600 text-white cursor-pointer hover:opacity-90'
            )}
          >
            {isGenerating ? t('common.generating') : t('composeIdeas.generateVariations')}
          </button>

          {generatedVariations.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg mb-4 text-text-primary">{t('composeIdeas.variations')}</h3>
              {generatedVariations.map((variation, index) => (
                <div
                  key={index}
                  onClick={() => handleCopy(variation.text, index)}
                  className="p-4 mb-3 bg-surface-2 border border-border rounded-xl cursor-pointer transition-all hover:border-accent hover:bg-accent/5"
                >
                  <div className="flex justify-between text-xs text-text-muted mb-2">
                    <span>
                      {copiedIndex === index ? `${t('common.copied')}` : t('composeIdeas.variationLabel', { n: index + 1, score: (variation.score * 100).toFixed(0) })}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); storageService.addSavedIdea(variation.text); }}
                      className="bg-transparent border-none text-text-muted cursor-pointer text-sm p-0 hover:text-brand"
                      title={t('common.savedToIdeas')}
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                    </button>
                  </div>
                  <p className="text-sm leading-[1.5] m-0 text-text-primary">{variation.text}</p>
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePostToX(variation.text, toast, t); }}
                      className="px-3 py-1.5 bg-surface-1 border border-border text-text-muted rounded-lg text-xs font-medium hover:bg-surface-3 hover:text-text-primary transition-all cursor-pointer"
                    >{t('common.postToX')}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
